import React, { Fragment, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Dialog, Transition } from '@headlessui/react';
import Image from 'next/image';
import Button from './Button';
import Alert from './Alert';
import Spinner from './Spinner';
import { useFlips } from '../hooks/useFlips';

const FlipsModal = ({ success, pending, fail, open, onClose, coinFlipResult }) => {
  const { choiceToWin } = useFlips();

  const [image, setImage] = useState('heads');

  useEffect(() => {
    let interval;

    if (coinFlipResult) {
      setImage(coinFlipResult);
      if (interval) clearInterval(interval);
      return;
    }

    if (pending) {
      interval = setInterval(() => {
        setImage((i) => (i === 'heads' ? 'tails' : 'heads'));
      }, 500);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [coinFlipResult, pending]);

  return (
    <Transition.Root show={open} as={Fragment} onClose={() => {}}>
      <Dialog as='div' className='relative z-50'>
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <div className='fixed inset-0 z-20 transition-opacity bg-opacity-90 bg-neutral-900' />
        </Transition.Child>
        <div className='fixed inset-0 z-20 overflow-y-auto'>
          <div className='flex items-center justify-center min-h-full p-4 text-center sm:items-center sm:p-0'>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
              enterTo='opacity-100 translate-y-0 sm:scale-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100 translate-y-0 sm:scale-100'
              leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
            >
              <Dialog.Panel
                className={`relative overflow-hidden text-left transition-all transform rounded-lg shadow-xl bg-neutral-800 sm:my-8 sm:max-w-lg sm:w-full`}
              >
                <div className='px-4 pt-5 pb-4 bg-neutral-800 sm:p-6'>
                  <div className='mb-8'>
                    <h2 className='text-center font-bold mb-2 text-2xl'>
                      {coinFlipResult ? `Coin landed on ${coinFlipResult}!` : 'Flipping coin...'}
                    </h2>
                    <p className='text-gray-400 text-lg text-center'>
                      You guessed <span className='text-white'>{choiceToWin}</span>.
                    </p>
                  </div>

                  <div className='h-[250px] w-[250px] relative mx-auto'>
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: image === 'heads' ? 1 : 0 }} className='absolute inset-0'>
                      <Image src='/flip-heads.png' alt='heads' width={1000} height={1000} />
                    </motion.div>
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: image === 'tails' ? 1 : 0 }} className='absolute inset-0'>
                      <Image src='/flip-tails.png' alt='heads' width={1000} height={1000} />
                    </motion.div>
                  </div>

                  {pending && (
                    <div className='flex justify-center mt-5 mb-8'>
                      <Spinner />
                    </div>
                  )}

                  <div className='flex justify-center items-center flex-col'>
                    {success && (
                      <div className='mt-5'>
                        <Alert>{success}</Alert>
                      </div>
                    )}

                    {fail && (
                      <div className='mt-5'>
                        <Alert type='danger'>{fail}</Alert>
                      </div>
                    )}

                    {(success || fail) && (
                      <div className='mt-10'>
                        <Button theme='outline' onClick={onClose}>
                          Done
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default FlipsModal;
